/*
  The MIT License

  Copyright (c) 2017-2019 EclipseSource Munich
  https://github.com/eclipsesource/jsonforms

  Permission is hereby granted, free of charge, to any person obtaining a copy
  of this software and associated documentation files (the "Software"), to deal
  in the Software without restriction, including without limitation the rights
  to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
  copies of the Software, and to permit persons to whom the Software is
  furnished to do so, subject to the following conditions:

  The above copyright notice and this permission notice shall be included in
  all copies or substantial portions of the Software.

  THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
  IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
  FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
  AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
  LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
  OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
  THE SOFTWARE.
*/
import isEmpty from 'lodash/isEmpty';
import React, { ComponentType, Fragment } from 'react';
import type Ajv from 'ajv';
import type { UISchemaElement } from '@jsonforms/core';
import {
  getAjv,
  JsonFormsCellRendererRegistryEntry,
  JsonFormsRendererRegistryEntry,
  JsonSchema,
  OwnPropsOfRenderer,
  getRuleValidator,
  RuleValidator,
} from '@jsonforms/core';
import { JsonFormsDispatch, useJsonForms } from '@jsonforms/react';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';

export const renderLayoutElements = (
  elements: UISchemaElement[],
  schema: JsonSchema,
  path: string,
  enabled: boolean,
  renderers?: JsonFormsRendererRegistryEntry[],
  cells?: JsonFormsCellRendererRegistryEntry[],
  direction: 'row' | 'column' = 'row'
) => {
  return elements.map((child, index) => {
    const dispatch = (
      <JsonFormsDispatch
        uischema={child}
        schema={schema}
        path={path}
        enabled={enabled}
        renderers={renderers}
        cells={cells}
      />
    );

    return direction === 'row' ? (
      <Grid key={`${path}-${index}`} size='grow'>
        {dispatch}
      </Grid>
    ) : (
      <Fragment key={`${path}-${index}`}>{dispatch}</Fragment>
    );
  });
};

export interface MaterialLayoutRendererProps extends OwnPropsOfRenderer {
  elements: UISchemaElement[];
  direction: 'row' | 'column';
}
const MaterialLayoutRendererComponent = ({
  visible,
  elements,
  schema,
  path,
  enabled,
  direction,
  renderers,
  cells,
}: MaterialLayoutRendererProps) => {
  if (isEmpty(elements) || !visible) {
    return null;
  }

  const children = renderLayoutElements(
    elements,
    schema,
    path,
    enabled,
    renderers,
    cells,
    direction
  );

  return direction === 'row' ? (
    <Grid container spacing={2}>
      {children}
    </Grid>
  ) : (
    <Stack>{children}</Stack>
  );
};
export const MaterialLayoutRenderer = React.memo(
  MaterialLayoutRendererComponent
);

export interface AjvProps {
  ajv: Ajv;
  /**
   * What rule conditions are evaluated with: the configured Form Validator
   * when a custom one is set, otherwise the Ajv instance.
   */
  ruleValidator?: RuleValidator;
}

// TODO fix @typescript-eslint/ban-types
// eslint-disable-next-line @typescript-eslint/ban-types
export const withAjvProps = <P extends {}>(
  Component: ComponentType<AjvProps & P>
) =>
  function WithAjvProps(props: P) {
    const ctx = useJsonForms();
    const state = { jsonforms: { ...ctx } };
    const ajv = getAjv(state);
    const ruleValidator = getRuleValidator(state);

    return <Component {...props} ajv={ajv} ruleValidator={ruleValidator} />;
  };

export interface MaterialLabelableLayoutRendererProps
  extends MaterialLayoutRendererProps {
  label?: string;
}
